// filterHelpers — VQE-Edit shared helpers for the filter edit slice.
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
// filter (WHERE / HAVING).
//
// Centralizes the structured-form → predicate-text formatter used by the
// FilterBuilder popover, so addFilter (and tests) can compose pieces the same
// way the popover commits them.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { WhereCombinator } from '../../../../sql-resolver/types';

import { formatJsonFilterText } from './jsonFilterEmit';

/**
 * The popover form returns a structured value; tests can compose one inline
 * to exercise addFilter without round-tripping through the React UI. The
 * `value` field is opaque text — VQE-Edit does not validate predicate syntax
 * (matches today's Query Editor contract — server error surfaces if wrong).
 *
 * JCS D3: optional `path` (set when the selected column is JSON-classified per
 * D2) + `dialect` (the resolver's engine id) drive engine-correct emit for
 * JSON WHERE filters. Absent fields preserve the byte-for-byte D2 / pre-D2
 * shape (`col op value`).
 */
export type FilterFormValue = {
  readonly column: string;
  readonly operator: string;
  /**
   * Right-hand side text. For unary operators (IS NULL, IS NOT NULL) the
   * empty string is acceptable — `formatFilterText` skips it. For
   * `IN`/`NOT IN`/`BETWEEN` the user supplies the parenthesized / paired
   * literal text directly (no auto-wrapping in v1).
   */
  readonly value: string;
  readonly path?: string;
  readonly dialect?: SqlEngine;
};

/** Operators we recognize as unary (no RHS). */
const UNARY_OPERATORS = new Set(['IS NULL', 'IS NOT NULL']);

/**
 * Stringify a structured filter value for SR-Edit consumption. The output is
 * intentionally simple: `<column> <operator> <value>` (or `<column> <operator>`
 * for unary operators). VQE-Edit does NOT quote identifiers or values — the
 * caller is responsible for engine-correct quoting (the FilterBuilder will
 * eventually wire `QualifiedName.quoted`-aware columns through).
 *
 * JCS D3: when `path` is non-empty AND `dialect` is supplied, the predicate
 * is built through the engine-specific JSON emit dispatcher in
 * `jsonFilterEmit.ts` — this covers JSON_VALUE / JSON_EXISTS / `->` / `@>` /
 * dot-notation / etc. across the five engines. Non-path operators (`=`,
 * `<>`, `IS NULL`, …) on JSON columns silently fall back to the simple shape
 * because they're identical to the non-JSON case.
 */
export function formatFilterText(value: FilterFormValue): string {
  if (
    value.path !== undefined &&
    value.path.length > 0 &&
    value.dialect !== undefined
  ) {
    return formatJsonFilterText(
      {
        column: value.column,
        operator: value.operator.trim(),
        value: value.value,
        path: value.path,
      },
      value.dialect,
    ).trim();
  }
  const op = value.operator.trim();
  if (UNARY_OPERATORS.has(op.toUpperCase())) {
    return `${value.column} ${op}`.trim();
  }
  return `${value.column} ${op} ${value.value}`.trim();
}

/** Validates a combinator string at runtime; throws for anything else. */
export function assertWhereCombinator(value: unknown): WhereCombinator {
  if (value === 'AND' || value === 'OR') return value;
  throw new Error(
    `addFilter: combinator must be 'AND' or 'OR' (got ${JSON.stringify(value)})`,
  );
}
